import { STATUSES, type Status } from '@krit/core';

/**
 * Shared pipeline-stage metadata.
 *
 * The CRM board and the deal-header stage picker have to agree on what the pipeline looks like —
 * the order of stages, which two are terminal, what colour each one is, and the fact that
 * `Invested` is shown as "Done". This module is the single definition; both import from here so a
 * new stage or a recoloured dot lands in both places at once.
 */

/** The terminal stages. A deal here has left the live pipeline (won or dropped). */
export const CLOSED_STAGES: readonly Status[] = ['Invested', 'Not Interested'];
const CLOSED_SET = new Set<string>(CLOSED_STAGES);
export const isClosedStage = (s: string) => CLOSED_SET.has(s);

/** The live pipeline, in order. Excludes Intake (the WhatsApp inbox) and the terminal stages. */
export const ACTIVE_STAGES: Status[] = STATUSES.filter((s) => s !== 'Intake' && !CLOSED_SET.has(s));

/** Display-only rename. The stored status stays `Invested` — Portfolio keys off it. */
const STAGE_LABEL: Record<string, string> = { Invested: 'Done' };
export const stageLabel = (s: string) => STAGE_LABEL[s] ?? s;

/** Dot colour per stage (solid — for the board's lane headers and the header pill). */
export const STAGE_DOT: Record<string, string> = {
  Open: 'bg-muted-2',
  'Under Review': 'bg-teal',
  Interested: 'bg-green',
  'In Discussion': 'bg-purple',
  Diligence: 'bg-amber',
  'IC Review': 'bg-purple',
  'Term Sheet': 'bg-gold',
  Invested: 'bg-green',
  'Not Interested': 'bg-red',
  Intake: 'bg-gold',
};

/** What reaching a terminal stage actually means, shown under the CLOSED group in the picker. */
export const CLOSED_NOTE: Record<string, string> = {
  Invested: 'closed won · graduates to Portfolio monitoring',
  'Not Interested': 'dropped · kept for the record',
};
